import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { tokens, makeStyles, mergeClasses } from '@fluentui/react-components';
import { I18nModel } from '@bingads-webui/copilot-i18n';
import { ISubCopilotProps, ITextAsset, Tone } from './types.d';
import { ITextSuggestionListProps, TextSuggestionList } from './TextSuggestionList';
import { InlineCopilotDialog } from './InlineCopilotDialog';
import { ErrorMessageBar } from './ErrorMessageBar';
import { CopilotTab, CopilotTabItem } from './CopilotTab';
import { Loading } from './Loading';
import {
  LOADING_TEXT, REPLACE_BTN_TEXT, UPDATED_RECOMMENDATION_TEXT, NO_OPTIONS_TEXT,
} from './staticTexts';

const useDifferentToneLoadingStyles = makeStyles({
  root: {},
  tonePlaceholder: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  loading: {
    display: 'flex',
    marginTop: tokens.spacingVerticalM,
    justifyContent: 'flex-start',
  },
});

interface IDifferentToneLoadingProps {
  i18n: I18nModel;
}

const DifferentToneLoading: React.FC<IDifferentToneLoadingProps> = React.memo(({ i18n }) => {
  const styles = useDifferentToneLoadingStyles();

  return (
    <div className={mergeClasses(styles.root, 'different-tone-loading')}>
      <div className={styles.loading}>
        <Loading loadingText={i18n.getString(LOADING_TEXT)} />
      </div>
    </div>
  );
});

const useDifferentToneStyles = makeStyles({
  root: {},
  errorTip: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  errorMessage: {
    marginTop: tokens.spacingVerticalL,
  },
});

interface ITone {
  tone: Tone;
  text: string;
}

interface IDifferentToneProps extends ISubCopilotProps {
  onToneSelected?: (tone: string) => void;
  tones: ITone[];
}

export const DifferentTone: React.FC<IDifferentToneProps> = React.memo((props) => {
  const styles = useDifferentToneStyles();
  const {
    assets,
    error,
    loading,
    i18n,
    onSelect,
    open,
    onOpenChange,
    onRequestAsset,
    onClearAsset,
    tones,
    title,
    closeButtonLabel,
    modalType,
    onToneSelected,
    isNewLogo,
  } = props;
  const [activeTone, setActiveTone] = useState(tones[0].tone);
  const cachedSuggestions = useRef<Record<string, ITextAsset[] | null>>({
    [activeTone]: assets,
  });
  const [suggestionsToRender, setSuggestionsToRender] = useState<ITextAsset[]>([]);

  const onActiveChange = useCallback((key: string) => {
    // load from cache first, otherwise call clear asset to reload the target tone suggestions
    if (cachedSuggestions.current[key] == null) {
      setSuggestionsToRender([]);
      onClearAsset();
    } else {
      setSuggestionsToRender(cachedSuggestions.current[key] as ITextAsset[]);
    }
    setActiveTone(key as Tone);
  }, [cachedSuggestions, onClearAsset]);
  const onSelectCallback = useCallback<ITextSuggestionListProps['onSelect']>(({ value }) => {
    onSelect({ value: [{ text: value }] });
  }, [onSelect]);
  const onOpenWithResetActivatedTone = useCallback<NonNullable<typeof onOpenChange>>((...args) => {
    setActiveTone(tones[0].tone);
    if (onOpenChange) {
      onOpenChange(...args);
    }
  }, [onOpenChange, tones]);

  // Clear all cached assets while changing open status
  useEffect(() => {
    cachedSuggestions.current = {};
  }, [open]);

  useEffect(() => {
    if (open) {
      if (assets == null && !error && !cachedSuggestions.current[activeTone]) {
        onRequestAsset({
          requestType: 'differentTone',
          activeTone,
        });
      } else if (assets != null && cachedSuggestions.current[activeTone] == null) {
        cachedSuggestions.current[activeTone] = assets;
        setSuggestionsToRender(assets);
      }
    }
  }, [activeTone, assets, error, onRequestAsset, open]);

  useEffect(() => {
    if (open) {
      onToneSelected?.(activeTone);
    }
  }, [activeTone, open, onToneSelected]);

  const isSuggestionsEmpty = suggestionsToRender.length === 0;

  return (
    <InlineCopilotDialog
      open={open}
      onOpenChange={onOpenWithResetActivatedTone}
      title={title}
      closeButtonLabel={closeButtonLabel}
      modalType={modalType}
      isNewLogo={isNewLogo}
    >
      <div className={mergeClasses(styles.root, 'different-tone-dialog')}>
        <CopilotTab active={activeTone} onActiveChange={onActiveChange}>
          {tones.map((tone) => (
            <CopilotTabItem tabKey={tone.tone} key={tone.tone}>
              {tone.text}
            </CopilotTabItem>
          ))}
        </CopilotTab>
        {!isSuggestionsEmpty
          ? (
            <TextSuggestionList
              suggestions={suggestionsToRender as ITextAsset[]}
              description={i18n.getString(UPDATED_RECOMMENDATION_TEXT)}
              onSelect={onSelectCallback}
              buttonText={i18n.getString(REPLACE_BTN_TEXT)}
            />
          )
          : null}
        {isSuggestionsEmpty && assets != null && !loading && !error && (
          <h3 className={styles.errorTip}>{i18n.getString(NO_OPTIONS_TEXT)}</h3>
        )}
        {error
          && (
          <>
            <h3 className={styles.errorTip}>{i18n.getString(NO_OPTIONS_TEXT)}</h3>
            <ErrorMessageBar className={styles.errorMessage}>{error}</ErrorMessageBar>
          </>
          )}
        {loading && isSuggestionsEmpty && <DifferentToneLoading i18n={i18n} />}
      </div>
    </InlineCopilotDialog>
  );
});
